import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  HomeIcon,
  CreditCardIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  CheckIcon,
  ArrowRightIcon,
  PhoneIcon,
  CalendarIcon,
  DevicePhoneMobileIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';
import MarketingHero from '../components/marketing/MarketingHero';
import MarketingCtaBand from '../components/marketing/MarketingCtaBand';

const ProductsPage = () => {
  const [activeTab, setActiveTab] = useState('personal');

  const products = {
    personal: [
      {
        name: 'Everyday Checking',
        description: 'No-fee checking with powerful digital banking features',
        icon: BanknotesIcon,
        features: ['No monthly fees', 'Free ATM access', 'Mobile check deposit'],
        rate: 'No minimum balance',
        popular: true,
        cta: '/signup',
      },
      {
        name: 'High-Yield Savings',
        description: 'Earn more with our competitive savings rates',
        icon: BuildingLibraryIcon,
        features: ['Competitive APY', 'No monthly fees', 'Automatic savings tools'],
        rate: 'Ask for current APY',
        cta: '/signup',
      },
      {
        name: 'Premium Credit Card',
        description: 'Earn rewards on every purchase with our premium card',
        icon: CreditCardIcon,
        features: ['Cashback rewards', 'No annual fee', 'Travel protections'],
        rate: 'Intro APR available',
        cta: '/contact',
      },
      {
        name: 'Personal Loans',
        description: 'Flexible financing for your personal needs',
        icon: CurrencyDollarIcon,
        features: ['Competitive rates', 'Quick decisions', 'No prepayment penalties'],
        rate: 'Rates from 5.99% APR',
        cta: '/contact',
      },
      {
        name: 'Home Mortgage',
        description: 'Make your dream home a reality',
        icon: HomeIcon,
        features: ['Low down payment options', 'Competitive rates', 'Local guidance'],
        rate: 'Ask for current rates',
        cta: '/contact',
      },
      {
        name: 'Investment Account',
        description: 'Start building your wealth today',
        icon: ChartBarIcon,
        features: ['$0 commission trades', 'Professional advice', 'Research tools'],
        rate: '$0 commission trades',
        cta: '/contact',
      },
    ],
    business: [
      {
        name: 'Business Checking',
        description: 'Powerful tools for your growing business',
        icon: BanknotesIcon,
        features: ['Free transactions', 'Business debit card', 'Online bill pay'],
        rate: 'First year free',
        popular: true,
        cta: '/signup',
      },
      {
        name: 'Merchant Services',
        description: 'Accept payments anywhere, anytime',
        icon: CreditCardIcon,
        features: ['Low transaction fees', 'Next-day deposits', 'Mobile POS'],
        rate: 'Transparent pricing',
        cta: '/contact',
      },
      {
        name: 'Business Credit Card',
        description: 'Manage expenses and earn rewards',
        icon: CreditCardIcon,
        features: ['Cashback rewards', 'Employee cards', 'Expense tracking'],
        rate: 'No annual fee',
        cta: '/contact',
      },
      {
        name: 'Business Loans',
        description: 'Fuel your business growth',
        icon: CurrencyDollarIcon,
        features: ['Fast funding', 'Flexible terms', 'Competitive rates'],
        rate: 'Rates from 6.99% APR',
        cta: '/contact',
      },
      {
        name: 'Commercial Real Estate',
        description: 'Finance your business property',
        icon: BuildingLibraryIcon,
        features: ['Flexible structures', 'Fixed rate options', 'Expert guidance'],
        rate: 'Ask for current rates',
        cta: '/contact',
      },
      {
        name: 'Treasury Management',
        description: 'Optimize your cash flow',
        icon: PresentationChartLineIcon,
        features: ['Cash forecasting', 'Fraud protection', 'Payment automation'],
        rate: 'Custom pricing',
        cta: '/contact',
      },
    ],
  };

  const benefits = [
    {
      title: 'NCUA insured',
      description: 'Deposits protected up to applicable NCUA limits.',
      icon: ShieldCheckIcon,
    },
    {
      title: 'Member value',
      description: 'Competitive rates and fewer fees as a not-for-profit cooperative.',
      icon: StarIcon,
    },
    {
      title: 'Local guidance',
      description: 'Real specialists for accounts, loans, and everyday banking questions.',
      icon: UserGroupIcon,
    },
    {
      title: 'Digital ready',
      description: 'Mobile deposit, bill pay, and secure transfers when you need them.',
      icon: DevicePhoneMobileIcon,
    },
  ];

  const ProductCard = ({ product }) => (
    <article className="panel-lift group flex h-full flex-col border border-oak-100 p-7 sm:p-8">
      {product.popular ? (
        <p className="mb-4 inline-flex w-fit bg-oak-900 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-claire-200">
          Most popular
        </p>
      ) : (
        <div className="mb-4 h-6" aria-hidden />
      )}
      <div className="flex h-12 w-12 items-center justify-center bg-mist text-claire-700 transition duration-500 group-hover:bg-claire-700 group-hover:text-white">
        <product.icon className="h-6 w-6" aria-hidden />
      </div>
      <h3 className="mt-6 font-display text-2xl font-semibold text-oak-900">{product.name}</h3>
      <p className="mt-3 leading-relaxed text-oak-600">{product.description}</p>
      {product.rate ? (
        <p className="mt-5 border-l-2 border-claire-400 pl-3 text-sm font-medium text-oak-700">{product.rate}</p>
      ) : null}
      <ul className="mt-6 flex-1 space-y-3 border-t border-oak-100 pt-6">
        {product.features.map((feature) => (
          <li key={feature} className="flex items-start text-sm text-oak-700">
            <CheckIcon className="mr-3 mt-0.5 h-4 w-4 shrink-0 text-claire-600" aria-hidden />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <Link
        to={product.cta}
        className="mt-8 inline-flex items-center justify-center bg-oak-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-claire-700"
      >
        {product.cta === '/signup' ? 'Get started' : 'Learn more'}
        <ArrowRightIcon className="ml-2 h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
      </Link>
    </article>
  );

  return (
    <div className="min-h-screen bg-mist text-oak-900">
      <Navigation />

      <MarketingHero
        title="Accounts & lending"
        titleAccent="for every chapter"
        description="Products designed around members, whether you are growing personally or scaling a business."
      >
        <div className="mt-10 inline-flex gap-1 border border-white/25 bg-black/20 p-1.5 backdrop-blur-md">
          <button
            type="button"
            onClick={() => setActiveTab('personal')}
            className={`px-6 py-2.5 text-sm font-semibold transition sm:px-8 ${
              activeTab === 'personal' ? 'bg-white text-oak-900 shadow-sm' : 'text-white/90 hover:bg-white/10'
            }`}
          >
            Personal
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('business')}
            className={`px-6 py-2.5 text-sm font-semibold transition sm:px-8 ${
              activeTab === 'business' ? 'bg-white text-oak-900 shadow-sm' : 'text-white/90 hover:bg-white/10'
            }`}
          >
            Business
          </button>
        </div>
      </MarketingHero>

      <section className="relative z-10 -mt-8 border-b border-oak-100 bg-white px-4 py-10 shadow-soft sm:-mt-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-2 lg:grid-cols-4">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="flex gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-mist text-claire-700">
                <benefit.icon className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <h3 className="font-semibold text-oak-900">{benefit.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-oak-600">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-leaf-vein px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="mb-14 max-w-2xl">
            <div className="section-rule mb-5" />
            <h2 className="font-display text-3xl font-semibold text-oak-900 sm:text-4xl lg:text-5xl">
              {activeTab === 'personal' ? 'Personal banking' : 'Business banking'}
            </h2>
            <p className="mt-4 text-lg text-oak-600">
              {activeTab === 'personal'
                ? 'Everyday accounts, borrowing, and guidance built for real life.'
                : 'Cash management, lending, and payment tools that scale with your team.'}
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {products[activeTab].map((product) => (
              <ProductCard key={product.name} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-oak-100 bg-oak-950 py-20 text-white sm:py-24">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center font-display text-3xl font-semibold sm:text-4xl">Need help choosing?</h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-oak-300">
            Local specialists will walk you through options. No pressure, no runaround.
          </p>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            <Link
              to="/contact"
              className="group border border-white/10 bg-white/5 p-8 text-center backdrop-blur-sm transition hover:-translate-y-1 hover:border-claire-300/40 hover:bg-white/10"
            >
              <PhoneIcon className="mx-auto h-8 w-8 text-claire-300 transition group-hover:-translate-y-0.5" aria-hidden />
              <h3 className="mt-4 font-display text-lg font-semibold">Call us</h3>
              <p className="mt-2 text-sm text-oak-300">Speak with a product specialist.</p>
            </Link>
            <Link
              to="/contact"
              className="group border border-white/10 bg-white/5 p-8 text-center backdrop-blur-sm transition hover:-translate-y-1 hover:border-claire-300/40 hover:bg-white/10"
            >
              <BuildingLibraryIcon className="mx-auto h-8 w-8 text-claire-300 transition group-hover:-translate-y-0.5" aria-hidden />
              <h3 className="mt-4 font-display text-lg font-semibold">Visit a branch</h3>
              <p className="mt-2 text-sm text-oak-300">Find hours and directions for your visit.</p>
            </Link>
            <Link
              to="/contact"
              className="group border border-white/10 bg-white/5 p-8 text-center backdrop-blur-sm transition hover:-translate-y-1 hover:border-claire-300/40 hover:bg-white/10"
            >
              <CalendarIcon className="mx-auto h-8 w-8 text-claire-300 transition group-hover:-translate-y-0.5" aria-hidden />
              <h3 className="mt-4 font-display text-lg font-semibold">Schedule time</h3>
              <p className="mt-2 text-sm text-oak-300">Request a lending or business consultation.</p>
            </Link>
          </div>
        </div>
      </section>

      <MarketingCtaBand
        heading="Ready to get started?"
        body="Join Claireoaks Credit Union and put local decisions, member value, and modern tools on your side."
        primaryLabel="Open an account"
        secondaryLabel="Talk with us"
      />

      <Footer />
    </div>
  );
};

export default ProductsPage;
